import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import moment from 'moment';
import isEqual from 'lodash/isEqual'; // Add this import at the top

import { useAuth } from '../../../../firebase/AuthProvider';
import { getAllEmployees, getAllEvents, getAllUserTask, updateEvent } from '../../../../services/calendarService/EventService';
import { getTaskById, getTaskParticipant, getTasks } from '../../../../services/taskServices/TaskService';
import { AllEvent, AllUsers, userTask } from '../../../../interfaces/identity/calendar/Events';
import Loader from '../../loader/Loader';
import calenderlogo from '../../../../assets/images/calenderlogo.svg';
import ParticipantSelectDropdown from '../../../ui/Dropdown/ParticipantSelectDropdown';
import RecurrenceSelector from './RecurrenceSelector'; // Import the new component
import { RRule } from 'rrule'; // Import RRule
import ScopeModal from './ScopeModal'; // Import ScopeModal
import CustomDropdown from '../CustomDropdown';
import CustomInput from '../CustomInput';
import AddPlace from "../../../../assets/images/Add-Place.svg";
import AddDate from "../../../../assets/images/Add-Date.svg";
import AddTime from "../../../../assets/images/Add-Time.svg";
import AddTask from "../../../../assets/images/Add-Task.svg";
import { GetUserWhoNotHaveEmails } from '../../../../utilities/common/commonFuntion';
import LinkIconMeet from "../../../../assets/images/LinkIconMeet.svg";
import Title from "../../../../assets/images/AddEventTitle.png";
import TimePicker from '../TimePicker';
import AddNotes from "../../../../assets/images/AddNotes.svg";
import TaskDropdown from '../TaskDropdown';
import TaskFilters from '../../../../interfaces/task/TaskFilters';
import Tag from '../../../../interfaces/tag/Tag';


interface EventData {
    title: string;
    startTime: string;
    endTime: string;
    description: string;
    location: string;
    meetingDetails: string;
    ownerUserID: string;
    participantUserIds: string[];
    externalUserEmails: string[];
    taskId: number;
    isSprout: boolean;
    recurrenceRule?: string; // Add recurrenceRule
    time_zone?: string; // Add time_zone
    parentEventId?: string; // Add parentEventId
}

const generateTimeOptions = () => {
    const times = [];
    let time = moment().startOf('day');
    const endOfDay = moment().endOf('day');

    while (time.isSameOrBefore(endOfDay)) {
        times.push(time.format("h:mm A"));
        time.add(15, 'minutes');
    }
    return times;
};

const timeOptions = generateTimeOptions();

export default function EditEvent({ event, onClose, onEventCreated }: {
    event: AllEvent | null
    onClose?: () => void;
    onEventCreated?: () => void;
}) {
    const { currentUser, currentOrganization } = useAuth();
    const userId = (currentUser as any)?.uid;
    const token = (currentUser as any)?.accessToken;
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [employees, setEmployees] = useState<AllUsers[]>([]);
    const [userTasks, setUserTasks] = useState<userTask[]>([]);
    const [filteredTasks, setFilteredTasks] = useState<userTask[]>([]);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [taskLoadError, setTaskLoadError] = useState<string>("");
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get current timezone

    const [showEditScopeModal, setShowEditScopeModal] = useState(false); // New state for edit scope modal
    const [pendingFormData, setPendingFormData] = useState<EventData | null>(null); // To store form data before scope selection

    const [selectedDate, setSelectedDate] = useState<string>(
        event?.startTime ? moment(event.startTime).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")
    );

    const [formData, setFormData] = useState<EventData>({
        title: '',
        startTime: '',
        endTime: '',
        description: '',
        location: '',
        meetingDetails: '',
        ownerUserID: userId,
        participantUserIds: [],
        externalUserEmails: [],
        taskId: 0,
        isSprout: true,
        recurrenceRule: event?.recurrenceRule || undefined, // Initialize recurrenceRule
        time_zone: event?.time_zone || currentTimeZone, // Initialize time_zone
    });
    

    // Store the original event data for dirty check
    const [originalFormData, setOriginalFormData] = useState<EventData | null>(null);

    const handleParticipantsChange = async (data: { participantUserIds: string[]; externalUserEmails: string[] }) => {
        setFormData(prev => ({
            ...prev,
            participantUserIds: data.participantUserIds,
            externalUserEmails: data.externalUserEmails,
        }));

        // Load tasks for the selected participants
        const participantIds = data.participantUserIds.map(id => Number(id));
        if (participantIds.length > 0) {
            await loadTasksForParticipant(participantIds);
        } else {
            await loadTasksForParticipant(null);
        }
    };

    const handleRecurrenceChange = (rruleString?: string) => {
        setFormData(prev => ({
            ...prev,
            recurrenceRule: rruleString,
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTimeChange = (name: string, value: string | number) => {
        if (typeof value !== 'string') return;
        setFormData((prev) => {
            const updatedFormData = { ...prev, [name]: value };
            if (name === "startTime" && value) {
                const startTimeMoment = moment(value, "h:mm A");
                const endTimeMoment = startTimeMoment.add(30, 'minutes');
                updatedFormData.endTime = endTimeMoment.format("h:mm A");
            }
            return updatedFormData;
        });
    };

    const handleTaskChange = async (value: string | number) => {
        const taskId = typeof value === "number" ? value : 0;
        setLoadingTasks(true);
        setTaskLoadError("");

        if (taskId !== 0) {
            try {
                if (token) {
                    // Call GET /api/task/participant?taskId={taskId} as per requirements
                    const taskParticipant = await getTaskParticipant(taskId.toString(), token);

                    if (taskParticipant) {
                        // Check if the participant is deactivated
                        if (taskParticipant.deactivated) {
                            // Don't select deactivated participants
                            toast.info("The participant for this task is deactivated and cannot be selected.");
                        } else {
                            const participantIdToAdd = taskParticipant.id;
                            
                            // Check if this participant is already selected
                            const alreadyExists = formData.participantUserIds.includes(String(participantIdToAdd));
                            
                            // Only add participant if not already selected
                            if (!alreadyExists) {
                                setFormData(prev => ({
                                    ...prev,
                                    taskId,
                                    participantUserIds: [...prev.participantUserIds, String(participantIdToAdd)],
                                }));
                            } else {
                                setFormData(prev => ({
                                    ...prev,
                                    taskId,
                                }));
                            }
                        }
                    } else {
                        // Task participant fetch failed
                        setTaskLoadError("Unable to load participant for selected task.");
                    }
                } else {
                    console.error("Auth token is not available.");
                }
            } catch (error) {
                console.error("Error fetching task participant:", error);
                setTaskLoadError("Unable to load participant for selected task.");
            }
        } else {
            // Task cleared
            setFormData(prev => ({ ...prev, taskId }));
        }

        setLoadingTasks(false);
    };

    async function fetchAllEmployee() {
        if (!currentUser) return;
        setLoading(true);
        try {
            const response = await getAllEmployees(token);
            if (response) {
                setEmployees(response);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    }

    async function fetchAllEvents() {
        if (!currentUser) return;
        setLoading(true);
        try {
            const start = moment().subtract(1, 'year').toDate();
            const end = moment().add(1, 'year').toDate();
            await getAllEvents(token, start, end);
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setLoading(false);
        }
    }
    async function fetchAllUserTasks() {
        if (!currentUser || !token) return;
        setLoading(true);
        try {
            const response = await getAllUserTask(token);
            if (response) {
                setUserTasks(response);
                setFilteredTasks(response);
            }
        } catch (error) {
            console.error("Error fetching user tasks:", error);
        } finally {
            setLoading(false);
        }
    }

    async function loadTasksForParticipant(participantIds: number[] | null): Promise<userTask[]> {
        if (!currentUser || !token) return [];

        setLoadingTasks(true);
        setTaskLoadError("");

        try {
            if (participantIds === null || participantIds.length === 0) {
                setFilteredTasks(userTasks);
                setLoadingTasks(false);
                return userTasks;
            }

            const filters: TaskFilters = {
                participants: participantIds.map(id => id.toString()),
            };

            const result = await getTasks(
                token,
                undefined,
                1,
                100,
                filters,
                "",
                false,
                false,
                false,
                undefined
            );

            if (result && result.tasks) {
                const mappedTasks: userTask[] = result.tasks.map((task: any) => ({
                    id: Number(task.id),
                    title: task.title,
                    description: task.description || "",
                    assigneeRole: task.assigneeRoleName || "",
                    participants: task.participant ? [task.participant.id.toString()] : [],
                    tags: task.tags ? task.tags.map((tag: Tag) => tag.name) : [],
                    templateTasks: [],
                }));
                setFilteredTasks(mappedTasks);
                setLoadingTasks(false);
                return mappedTasks;
            } else {
                setFilteredTasks([]);
                setLoadingTasks(false);
                return [];
            }
        } catch (error) {
            console.error("Error loading tasks for participant:", error);
            setTaskLoadError("Unable to load tasks for selected participant.");
            setFilteredTasks([]);
            setLoadingTasks(false);
            return [];
        }
    }

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.startTime) newErrors.startTime = 'Start time is required';
        if (!formData.endTime) newErrors.endTime = 'End time is required';
        // if (formData.participantUserIds.length === 0 && formData.externalUserEmails.length === 0)
        //     newErrors.participantUserIds = "At least one participant is required";

        // Validate that meeting is not in the past
        if (selectedDate && formData.startTime) {
            const meetingDateTime = moment(`${selectedDate} ${formData.startTime}`, "YYYY-MM-DD h:mm A");
            const now = moment();

            if (meetingDateTime.isBefore(now)) {
                newErrors.startTime = 'Cannot schedule meetings in the past';
            }
        }

        // validate no email 
        if (GetUserWhoNotHaveEmails(employees, formData.participantUserIds).length > 0) {
            return newErrors.participantUserIds = "SomeParticipantNoEmail";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const executeSave = async (
        scope?: 'single' | 'future',
        formDataToSave?: EventData & { id: string }
    ) => {
        if (!token || !formDataToSave) {
            console.error("Missing auth token or form data.");
            toast.error("An unexpected error occurred.");
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const result = await updateEvent(formDataToSave, token, scope);
            if (result.success) {
                toast.success(result.message);
                if (onEventCreated) onEventCreated();
                if (onClose) onClose();
            } else {
                toast.error(result.message || "Failed to update event.");
            }
        } catch (error) {
            console.error("Error submitting event:", error);
            toast.error("An unexpected error occurred.");
        } finally {
            setLoading(false);
            setShowEditScopeModal(false);
            setPendingFormData(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (!token) {
            console.error("Missing auth token.");
            return;
        }
        if (!isDirty) {
            toast.info("No changes detected. Please modify the meeting before saving.");
            return;
        }

        const timezone = formData.time_zone || currentTimeZone;

        const startTime24hr = moment(formData.startTime, "h:mm A").format("HH:mm");
        const endTime24hr = moment(formData.endTime, "h:mm A").format("HH:mm");

        // Calculate start and end moments
        const startMoment = moment.tz(`${selectedDate} ${startTime24hr}`, timezone);
        let endMoment = moment.tz(`${selectedDate} ${endTime24hr}`, timezone);

        // If end time is less than or equal to start time, add 1 day to endMoment
        if (endMoment.isSameOrBefore(startMoment)) {
            endMoment.add(1, 'day');
        }

        const eventDataToSend = {
            ...formData,
            id: event?.id || "",
            startTime: startMoment.toISOString(),
            endTime: endMoment.toISOString(),
            time_zone: formData.time_zone,
            recurrenceRule: formData.recurrenceRule,
            parentEventId: event?.parentEventId,
        };

        if (event?.recurrenceRule || event?.parentEventId) {
            setPendingFormData(eventDataToSend as (EventData & { id: string }));
            setShowEditScopeModal(true);
        } else {
            // For non-recurring events, save directly
            await executeSave('single', eventDataToSend as (EventData & { id: string }));
        }
    };

    useEffect(() => {
        console.log("Event  : - : ", event);

        fetchAllEmployee();
        fetchAllUserTasks();
    }, []);

    // Load tasks for initial participants when event loads
    useEffect(() => {
        if (event && event.participantUserIds.length > 0) {
            const participantIds = event.participantUserIds.map(id => Number(id));
            loadTasksForParticipant(participantIds);
        }
    }, [event]);

    useEffect(() => {
        if (event) {
            const initialData: EventData = {
                title: event.title || '',
                startTime: moment.utc(event.startTime).local().format('h:mm A'),
                endTime: moment.utc(event.endTime).local().format('h:mm A'),
                description: event.description || '',
                location: event.location || '',
                meetingDetails: event.meetingDetails || '',
                ownerUserID: event.ownerUserID || userId,
                participantUserIds: event.participantUserIds || [],
                externalUserEmails: event.externalUserEmails || [],
                taskId: event.taskId ? Number(event.taskId) : 0,
                isSprout: event.isSprout,
                recurrenceRule: event.recurrenceRule || undefined,
                time_zone: event.time_zone || currentTimeZone,
                parentEventId: event.parentEventId || undefined,
            };
            setFormData(initialData);
            setOriginalFormData(initialData);
            setSelectedDate(moment.utc(event.startTime).local().format("YYYY-MM-DD"));
        }
    }, [event, userId]);

    // Dirty check: compare formData with originalFormData
    const isDirty = originalFormData ? !isEqual(
        { ...formData, meetingDate: selectedDate },
        { ...originalFormData, meetingDate: selectedDate }
    ) : false;

    const timeObjectOptions = timeOptions.map(time => ({ label: time, value: time }));
    let taskOptions = filteredTasks;
    const currentTask = userTasks.find(t => t.id === formData.taskId);
    if (currentTask && !filteredTasks.find(t => t.id === currentTask.id)) {
        taskOptions = [...filteredTasks, currentTask];
    }
    const taskObjectOptions = taskOptions.map((task) => ({
        label: task.title,
        value: task.id,
        taskId: `${task.id}`,
        participant: task.participants?.[0] ?? "N/A",
        friendlyId :  task.friendlyId
    }));

    const hasParticipants =
        formData.participantUserIds.length > 0 ||
        formData.externalUserEmails.length > 0;

    const participantCount =
        formData.participantUserIds.length + formData.externalUserEmails.length;

    const selectedParticipantLabels = employees
        .filter((user) => formData.participantUserIds.includes(user.userId))
        .map((user) => user.userName);

    const participantLabelParts = [
        ...selectedParticipantLabels,
        ...formData.externalUserEmails,
    ];

    return (
        <>
            {loading && <Loader />}
            <div style={{ maxWidth: "600px", margin: "0 auto" }}>
                <p className="!text-lg text-[#747474] font-medium px-7 pt-7">Edit Meeting</p>

                <div className="flex justify-center gap-3 mb-3.5">
                    <button
                        type="button"
                        onClick={() =>
                            setFormData((prev) => ({ ...prev, isSprout: false }))
                        }
                        className={`px-4 py-1.5 text-base font-normal rounded-md transition-all
      ${!formData.isSprout
                                ? "bg-[#F4F4F4] text-[#7E57C2] font-normal"
                                : "text-[#747474] hover:text-gray-700"
                            }`}
                    >
                        Waivera Meeting
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            setFormData((prev) => ({ ...prev, isSprout: true }))
                        }
                        className={`px-4 py-1.5 text-base font-normal rounded-md transition-all
      ${formData.isSprout
                                ? "bg-[#F4F4F4] text-[#7E57C2] font-normal"
                                : "text-[#747474] hover:text-gray-700"
                            }`}
                    >
                        External Meeting
                    </button>
                    
                </div>

                <form onSubmit={handleSubmit}>

                    <CustomInput
                        label="Add Event Title"
                        name="title"
                        placeholder="Add Event Title"
                        value={formData.title}
                        onChange={handleChange}
                        error={errors.title}
                        icon={Title}
                    />

                    <CustomInput
                        label="Location"
                        name="location"
                        placeholder={formData.isSprout ? "Location" : "Waivera"}
                        value={formData.location}
                        onChange={handleChange}
                        error={errors.location}
                        icon={formData.isSprout ? AddPlace : LinkIconMeet}
                        disabled={!formData.isSprout}
                    />
                    {/* <CustomDropdown
                        label="Task"
                        name="taskId"
                        value={formData.taskId}
                        onChange={handleTaskChange}
                        options={taskObjectOptions}
                        error={errors.taskId}
                    />
                    {hasParticipants && formData.taskId === 0 && (
                        <div className="mt-1 mb-1 pl-4 pr-4 text-xs text-warning col-span-2">
                            {participantLabelParts.map((label, index) => (
                                <span key={`${label}-${index}`} className="capitalize font-medium">
                                    {label}
                                    {index === participantLabelParts.length - 2 && " and "}
                                    {index < participantLabelParts.length - 2 && ", "}
                                </span>
                            ))}
                            <span className="font-medium">
                                {" "}participant{participantCount > 1 ? "s" : ""} added — please attach a task before saving.
                            </span>
                        </div>
                    )} */}
                    <div className="grid grid-cols-2">
                        <CustomInput
                            label="Meeting Date"
                            name="meeting"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            min={moment().format("YYYY-MM-DD")}
                            icon={AddDate}
                        />
                        <TimePicker
                            label="Start"
                            name="startTime"
                            value={formData.startTime}
                            onChange={(value) => handleTimeChange("startTime", value)}
                            // options={timeObjectOptions}
                            error={errors.startTime}
                            icon={AddTime}
                        />
                    </div>

                    <div className="grid grid-cols-2">
                        <RecurrenceSelector
                            value={formData.recurrenceRule}
                            onChange={handleRecurrenceChange}
                            startTime={moment(`${selectedDate} ${formData.startTime}`, "YYYY-MM-DD h:mm A").toISOString()}
                            timeZone={formData.time_zone || currentTimeZone}
                        />
                        <TimePicker
                            label="End"
                            name="endTime"
                            value={formData.endTime}
                            onChange={(value) => handleTimeChange("endTime", value)}
                            // options={timeObjectOptions}
                            error={errors.endTime}
                            icon={AddTime}
                        />
                    </div>
                    {/* <div className="grid grid-cols-2">
                            <CustomDropdown
                                label="Start Time"
                                name="startTime"
                                value={formData.startTime}
                                onChange={(value: string | number) => handleTimeChange("startTime", value)}
                                options={timeObjectOptions}
                                error={errors.startTime}
                                icon={AddTime}
                            />
                            <CustomDropdown
                                label="End Time"
                                name="endTime"
                                value={formData.endTime}
                                onChange={(value: string | number) => handleTimeChange("endTime", value)}
                                options={timeObjectOptions}
                                error={errors.endTime}
                                icon={AddTime}
                            />
                        </div> */}

                    {/* {!formData.isSprout ? (
                            <CustomInput
                                label="Title"
                                type="text"
                                name="location"
                                placeholder="Location"
                                value={formData.location}
                                onChange={handleChange}
                                icon={AddPlace}
                            />
                        ) : (
                            <div className="col-md-12 mt-3 px-5">
                                <div className="custom_input_flled mb-2">
                                    <label className='!text-[#747474] !text-[13px] 2xl:!text-[16px] !font-normal'>Meeting</label>
                                    <p className="text-muted italic text-xs">A meeting link will be auto-generated upon creation.</p>
                                </div>
                            </div>
                        )} */}
                    <ParticipantSelectDropdown
                        label="Participants"
                        name="participants"
                        participantUserIds={formData.participantUserIds}
                        externalUserEmails={formData.externalUserEmails}
                        onChange={handleParticipantsChange}
                        options={employees}
                        error={errors.participantUserIds}
                    />
                    <TaskDropdown
                        label="Task"
                        name="taskId"
                        value={formData.taskId}
                        onChange={handleTaskChange}
                        options={taskObjectOptions}
                        error={errors.taskId}
                        icon={AddTask}
                        loadingTasks={loadingTasks}
                    />
                    {taskLoadError && (
                        <div className="mt-1 mb-1 pl-4 pr-4 text-xs text-danger">
                            {taskLoadError}
                        </div>
                    )}
                    {hasParticipants && formData.taskId === 0 && (
                        <div className="mt-1 mb-1 pl-4 pr-4 text-xs text-warning col-span-2">
                            {participantLabelParts.map((label, index) => (
                                <span key={`${label}-${index}`} className="capitalize font-medium">
                                    {label}
                                    {index === participantLabelParts.length - 2 && " and "}
                                    {index < participantLabelParts.length - 2 && ", "}
                                </span>
                            ))}
                            <span className="font-medium">
                                {" "}participant{participantCount > 1 ? "s" : ""} added — please attach a task before saving.
                            </span>
                        </div>
                    )}

                    <CustomInput
                        name="description"
                        placeholder="Description"
                        value={formData.description}
                        onChange={handleChange}
                        type="textarea"
                        borderBotttom
                        icon={AddNotes}
                    />

                    <div className='modal_footer px-7 pb-7'>
                        <Button variant='secondary' onClick={onClose}>Cancel</Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={!isDirty || loading}
                        >
                            Save Changes
                        </Button>
                    </div>
                </form>
            </div>

            <ScopeModal
                isOpen={showEditScopeModal}
                onClose={() => { setShowEditScopeModal(false); setPendingFormData(null); }}
                onSelectScope={(scope) => executeSave(scope, pendingFormData as (EventData & { id: string }))}
                action="edit"
            />
        </>
    );
}